import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { fetchChatbotAccountRequests, resolveChatbotAccountRequest } from '@/lib/chatbotAsk';

const API_BASE_URL = (() => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'https://api.neogtm.com';
    }
  }
  return 'https://api.neogtm.com';
})();

function formatWhen(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}

export default function ChatbotAccountRequests({ currentUser, currentTenant }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const headers = {
    'x-tenant-id': currentTenant || currentUser?.tenant_id || '',
    'x-user-id': currentUser?.id || '',
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await fetchChatbotAccountRequests({
        apiBaseUrl: API_BASE_URL,
        status: 'open',
        headers,
      });
      setRows(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(e?.message || 'Could not load account requests');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [currentTenant, currentUser?.id, currentUser?.tenant_id]);

  useEffect(() => {
    load();
  }, [load]);

  const resolve = async (row, status) => {
    const id = row?.id ?? row?.request_id;
    if (!id) return;
    setBusyId(id);
    try {
      await resolveChatbotAccountRequest({
        apiBaseUrl: API_BASE_URL,
        requestId: id,
        headers,
        payload: {
          status,
          resolved_by: currentUser?.email || currentUser?.id || 'admin',
        },
      });
      await load();
    } catch (e) {
      setError(e?.message || 'Could not update request');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Chatbot account requests</CardTitle>
        <CardDescription>
          Open asks to add an account that is not in a user&apos;s workspace. Resolving does not add the account.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={load} disabled={loading}>
            Refresh
          </Button>
          {loading ? <span className="text-xs text-gray-500">Loading…</span> : null}
        </div>
        {error ? <p className="text-sm text-red-600">{error}</p> : null}
        {rows.length === 0 && !loading ? (
          <p className="text-sm text-gray-500">No open requests.</p>
        ) : (
          <div className="overflow-x-auto border rounded-md">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs text-gray-600">
                <tr>
                  <th className="px-3 py-2 font-medium">Account</th>
                  <th className="px-3 py-2 font-medium">Tenant</th>
                  <th className="px-3 py-2 font-medium">Reason</th>
                  <th className="px-3 py-2 font-medium">Question</th>
                  <th className="px-3 py-2 font-medium">Asked</th>
                  <th className="px-3 py-2 font-medium text-right"> </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const id = row.id ?? row.request_id;
                  return (
                    <tr key={id} className="border-t">
                      <td className="px-3 py-2 align-top">
                        <div className="font-medium text-[#0A1A2F]">{row.account_name || '—'}</div>
                        <div className="text-[11px] text-gray-500">{row.pool_account_id || ''}</div>
                      </td>
                      <td className="px-3 py-2 align-top">{row.tenant_id || '—'}</td>
                      <td className="px-3 py-2 align-top">{row.reason_code || '—'}</td>
                      <td className="px-3 py-2 align-top max-w-xs">
                        <p className="text-xs text-gray-700 whitespace-pre-wrap">{row.asked_question || row.question || '—'}</p>
                      </td>
                      <td className="px-3 py-2 align-top text-xs text-gray-500">{formatWhen(row.created_at)}</td>
                      <td className="px-3 py-2 align-top text-right whitespace-nowrap">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs mr-1"
                          disabled={busyId === id}
                          onClick={() => resolve(row, 'approved')}
                        >
                          Approve
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs"
                          disabled={busyId === id}
                          onClick={() => resolve(row, 'declined')}
                        >
                          Decline
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
